import { computed } from "vue";
import { queryOptions, useQuery, type QueryClient } from "@tanstack/vue-query";
import type { Permission, SessionResponse } from "@ai-platform/contracts";
import { callRoute, UnauthenticatedError } from "../api/client.ts";
import { routeKey } from "../api/query-keys.ts";
import { can, type Session } from "./access.ts";
import { SETTINGS_KEY } from "./settings.ts";

/** Key of the session in the query cache; the only place the session lives (ADR-076). */
export const SESSION_KEY = routeKey("session");

/**
 * What forgetting leaves in the cache: the session itself, which becomes `null`, and the portal's
 * settings, which belong to no user (ADR-133 point 3); read again, they would stand at their
 * defaults for a moment and open the catalogue to a guest who may not see it.
 */
const kept = (key: readonly unknown[]) => key[0] === SESSION_KEY[0] || key[0] === SETTINGS_KEY[0];

/**
 * `GET /api/auth/session`; without a session the API answers 401. For a guest that means
 * `null`. When the cache still holds a session, the 401 means it ended while the user was
 * working: the error goes on to the query client, which ends the session in the UI and opens the
 * login with the note (ADR-076 point 5, AP-F5). This covers the reload on window focus, which no
 * other read sees. Any other failure goes on as it is, and the cache keeps the last known session
 * (GP1); the query client reports an answer outside the contract (PF4).
 */
export async function fetchSession(client: QueryClient): Promise<SessionResponse | null> {
  const before = client.getQueryData<SessionResponse | null>(SESSION_KEY);
  try {
    return await callRoute("session");
  } catch (error) {
    if (error instanceof UnauthenticatedError && !before) return null;
    throw error;
  }
}

/**
 * The session the cache last knew, for when the API cannot tell (GP1): a 5xx, a `429` (GI1), a
 * network failure or an answer outside the contract says nothing about the session, so it is not
 * taken for "no session". `null` when no session was ever read.
 */
export function lastKnownSession(client: QueryClient): Session {
  return client.getQueryData<SessionResponse | null>(SESSION_KEY) ?? null;
}

/**
 * The session query. It reloads whenever the window gets focus, so a role or a suspension
 * changed elsewhere shows up, and every reload refreshes the hint cookie of the website (ADR-073,
 * ADR-076 point 5).
 */
export function sessionQueryOptions() {
  return queryOptions({
    queryKey: SESSION_KEY,
    queryFn: ({ client }) => fetchSession(client),
    refetchOnWindowFocus: "always",
  });
}

/**
 * Forgets every server datum and marks "no session" in one step (ADR-076 point 5), on logout
 * and when the session ended. The session query itself stays and gets `null`, so every
 * `useSession()` already on screen sees the change; `queryClient.clear()` would cut those
 * observers off and leave the old user in the header.
 */
export function forgetSession(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => !kept(query.queryKey) });
  queryClient.setQueryData(SESSION_KEY, null);
}

/**
 * Forgetting after a logout (ADR-083 point 5). Removing a query never tells its observers, so a
 * query a still mounted component watches would keep the signed-in user's data; the logout
 * therefore navigates to the catalogue first, so only public queries stay mounted, and those are
 * reset and fetched again as the guest. Everything without an observer goes, the session becomes
 * `null`. Not for the `401` path: there the mounted queries would fetch again before the login
 * page replaces them and run into the same `401`.
 */
export async function forgetSessionAfterLogout(queryClient: QueryClient): Promise<void> {
  queryClient.setQueryData(SESSION_KEY, null);
  queryClient.removeQueries({ type: "inactive", predicate: (query) => !kept(query.queryKey) });
  await queryClient.resetQueries({ type: "active", predicate: (query) => !kept(query.queryKey) });
}

/** The session for components: who is signed in and what they may do. */
export function useSession() {
  const query = useQuery(sessionQueryOptions());
  const session = computed<Session>(() => query.data.value ?? null);
  return {
    session,
    isPending: query.isPending,
    isGuest: computed(() => session.value === null),
    isStaff: computed(() => session.value?.staffRole != null),
    hasCustomer: computed(() => session.value?.customer != null),
    can: (action: Permission) => can(session.value, action),
  };
}
